import './bottom-sheet.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

const CLOSE_OFFSET = 120;
const CLOSE_VELOCITY = 500;
const EXIT_DURATION_MS = 280;
const ELASTIC_BOTTOM = 0.6;

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  maxHeight?: string;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  children,
  title,
  maxHeight,
}) => {
  const [rendered, setRendered] = useState(open);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  const dragState = useRef<{
    pointerId: number;
    startY: number;
    lastY: number;
    lastT: number;
    velocity: number;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setRendered(true);
      setClosing(false);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    if (rendered) {
      setClosing(true);
      setVisible(false);
      const timeout = setTimeout(() => {
        setRendered(false);
        setClosing(false);
      }, EXIT_DURATION_MS);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const applyDragY = useCallback((y: number) => {
    const node = sheetRef.current;
    if (!node) return;
    const clamped = y < 0 ? y * (1 - ELASTIC_BOTTOM) : y;
    node.style.transition = 'none';
    node.style.transform = `translateY(${clamped}px)`;
  }, []);

  const resetDragY = useCallback((animate: boolean) => {
    const node = sheetRef.current;
    if (!node) return;
    node.style.transition = animate ? '' : 'none';
    node.style.transform = '';
  }, []);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const drag = dragState.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const now = performance.now();
      const dt = now - drag.lastT;
      if (dt > 0) drag.velocity = ((e.clientY - drag.lastY) / dt) * 1000;
      drag.lastY = e.clientY;
      drag.lastT = now;
      applyDragY(e.clientY - drag.startY);
    },
    [applyDragY],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      const drag = dragState.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const offset = e.clientY - drag.startY;
      dragState.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);

      if (offset > CLOSE_OFFSET || drag.velocity > CLOSE_VELOCITY) {
        // Clear the drag-time inline override so the next render's
        // `--visible` removal can drive the exit via the base transition.
        resetDragY(true);
        onClose();
      } else {
        resetDragY(true);
      }
    },
    [handlePointerMove, onClose, resetDragY],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      dragState.current = {
        pointerId: e.pointerId,
        startY: e.clientY,
        lastY: e.clientY,
        lastT: performance.now(),
        velocity: 0,
      };
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    },
    [handlePointerMove, handlePointerUp],
  );

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    },
    [handlePointerMove, handlePointerUp],
  );

  if (!rendered) return null;

  return (
    <div className="bottom-sheet-portal">
      <div
        className={`bottom-sheet__backdrop${closing ? ' bottom-sheet__backdrop--out' : ' bottom-sheet__backdrop--in'}`}
        onClick={handleBackdropClick}
      />
      <div
        ref={sheetRef}
        className={`bottom-sheet${closing ? ' bottom-sheet--out' : ''}${visible ? ' bottom-sheet--visible' : ''}`}
        style={maxHeight ? { maxHeight } : undefined}>
        <div className="bottom-sheet__handle-area" onPointerDown={handlePointerDown}>
          <div className="bottom-sheet__handle" />
        </div>
        {title && <h3 className="bottom-sheet__title">{title}</h3>}
        <div className="bottom-sheet__content">{children}</div>
      </div>
    </div>
  );
};
